import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Download,
  FileText,
  Lock,
  Mail,
  Printer,
} from "lucide-react";
import mouLogo from "../assets/mouLogo.png";
import { useCfoBundle } from "../context/CfoLanguageContext";

const PDF_PATH = "/metodologia-diagnostico-financiero.pdf";
const PRINT_HTML_PATH = "/metodologia-diagnostico-financiero.html";
const NDA_PDF_PATH = "/acuerdo-confidencialidad-nda.pdf";
const NDA_MAILTO =
  "mailto:colaboradores@meu-aliado.com?subject=Solicitud%20Diagn%C3%B3stico%20Financiero%20-%20NDA&body=Buen%20d%C3%ADa%2C%0A%0ASolicito%20iniciar%20el%20proceso%20de%20Diagn%C3%B3stico%20Financiero%20(Fase%201).%0AAdjunto%20%2F%20solicito%20el%20Acuerdo%20de%20Confidencialidad%20(NDA).%0A%0AEmpresa%3A%0ANIT%3A%0AContacto%3A%0ACargo%3A%0ATel%C3%A9fono%3A%0A%0AQuedo%20atento%28a%29.%0A";

const confidentialitySteps = [
  {
    step: "1",
    title: "Enviar correo e iniciar el NDA",
    body: "Escriba a colaboradores@meu-aliado.com solicitando el Diagnóstico Financiero e indique los datos básicos de la empresa. Descargue el Acuerdo de Confidencialidad (NDA).",
  },
  {
    step: "2",
    title: "Remitir el NDA firmado",
    body: "La empresa firma el NDA y lo envía al mismo correo. Solo con el acuerdo firmado se habilita el intercambio de información.",
  },
  {
    step: "3",
    title: "Enviar la documentación",
    body: "Una vez validado el NDA, remita la información requerida según esta metodología (información general, percepción gerencial, estados financieros y demás ítems).",
  },
];

const phase1Sections = [
  {
    title: "1. Información general",
    items: [
      "Razón social, NIT y actividad económica",
      "Número aproximado de empleados y año de constitución",
      "Principales productos o servicios",
      "Segmentos de clientes y líneas de negocio",
    ],
    purpose: "Comprender el contexto, el modelo de negocio, el sector y la etapa de crecimiento.",
  },
  {
    title: "2. Percepción gerencial",
    items: [
      "Nombre los 5 principales problemas o retos que percibe actualmente",
      "¿Qué decisión financiera le preocupa más hoy?",
      "¿Cuáles son las prioridades de la gerencia en los próximos 12 meses?",
      "¿Dónde siente que falta visibilidad o control (caja, cartera, costos, rentabilidad, etc.)?",
    ],
    purpose: "Capturar la visión de la gerencia antes del análisis documental y priorizar el diagnóstico.",
  },
  {
    title: "3. Estados financieros",
    items: [
      "Preferiblemente de los últimos dos años y el año en curso",
      "Estado de Situación Financiera",
      "Estado de Resultados",
      "Flujo de Caja (si existe)",
    ],
    purpose: "Evaluar liquidez, rentabilidad, solidez financiera, endeudamiento y evolución del negocio.",
  },
  {
    title: "4. Cartera",
    items: ["Cuentas por cobrar", "Antigüedad de cartera"],
    purpose: "Analizar la capacidad de recuperación del efectivo y el riesgo de liquidez.",
  },
  {
    title: "5. Obligaciones financieras",
    items: ["Créditos", "Leasing", "Otras obligaciones"],
    purpose: "Comprender la estructura de financiación y la presión financiera.",
  },
  {
    title: "6. Cuentas por pagar",
    items: ["Proveedores principales", "Obligaciones pendientes"],
    purpose: "Evaluar el ciclo operativo de la empresa.",
  },
  {
    title: "7. Presupuesto (si existe)",
    items: ["Presupuesto anual", "Flujo de caja proyectado"],
    purpose: "Conocer el nivel de planeación financiera.",
  },
  {
    title: "8. Software utilizado",
    items: ["Software contable", "ERP", "Herramientas administrativas"],
    purpose: "Evaluar el nivel de madurez tecnológica del área financiera.",
  },
];

const phase1Deliverables = [
  { title: "Resumen ejecutivo", body: "Estado general de la organización." },
  { title: "Hallazgos principales", body: "Fortalezas detectadas." },
  { title: "Riesgos identificados", body: "Aspectos prioritarios que requieren atención." },
  { title: "Oportunidades de mejora", body: "Acciones de alto impacto." },
  { title: "Recomendaciones iniciales", body: "Hoja de ruta preliminar." },
];

const phase2Scope = [
  {
    title: "Planeación financiera",
    items: ["Presupuestos", "Proyecciones", "Modelos financieros"],
  },
  {
    title: "Tesorería",
    items: ["Flujo de caja", "Capital de trabajo", "Liquidez", "Políticas de pagos"],
  },
  {
    title: "Rentabilidad",
    items: ["Costos", "Márgenes", "Unidades de negocio", "Productos"],
  },
  {
    title: "Procesos financieros",
    items: ["Facturación", "Cartera", "Compras", "Pagos", "Controles internos"],
  },
  {
    title: "Riesgos",
    items: ["Riesgos financieros", "Riesgos operativos", "Riesgos de liquidez", "Riesgos de crecimiento"],
  },
  {
    title: "Tecnología",
    items: ["ERP", "Automatización", "Dashboards", "Indicadores", "Inteligencia de negocios"],
  },
  {
    title: "Gobierno financiero",
    items: ["Reportes gerenciales", "Indicadores", "Comités", "Seguimiento financiero"],
  },
];

const phase2ExtraDocs = [
  "Balance de prueba",
  "Auxiliares contables",
  "Declaraciones tributarias",
  "Contratos de financiación",
  "Presupuestos detallados",
  "Políticas internas",
  "Manuales de procesos",
  "Indicadores de gestión",
  "Información de nómina",
  "Información de inventarios",
  "Contratos relevantes",
  "Estructura organizacional",
  "Reportes comerciales y operativos",
];

const phase2Deliverables = [
  "Diagnóstico por áreas financieras",
  "Evaluación de riesgos y oportunidades",
  "Análisis de indicadores financieros",
  "Identificación de brechas en procesos y controles",
  "Plan de transformación financiera con acciones priorizadas",
  "Cronograma de implementación",
  "Recomendaciones estratégicas para la gerencia",
];

const finanzas360 = [
  { letter: "F", title: "Familiarización con el negocio" },
  { letter: "I", title: "Inspección financiera inicial" },
  { letter: "N", title: "Nivel de madurez financiera" },
  { letter: "A", title: "Análisis de riesgos y oportunidades" },
  { letter: "N", title: "Normalización y priorización" },
  { letter: "Z", title: "Zona de acción (plan de implementación)" },
  { letter: "A", title: "Acompañamiento estratégico" },
  { letter: "S", title: "Seguimiento y mejora continua" },
];

function DiagnosticoFinanciero() {
  const { cfo } = useCfoBundle();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const openPrintable = () => {
    window.open(PRINT_HTML_PATH, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="overflow-x-hidden bg-slate-50 print:bg-white">
      <section className="relative bg-[#0b1f3f] text-white overflow-hidden print:bg-white print:text-[#0b1f3f]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1f3f] via-[#123a72] to-[#1746a2] print:hidden" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 py-12 lg:py-16">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <img src={mouLogo} alt="MEU Aliado" className="h-12 sm:h-14 brightness-0 invert print:invert-0 print:brightness-100" />
            <div className="flex flex-wrap gap-3 print:hidden">
              <a
                href={PDF_PATH}
                download
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
              >
                <Download className="w-4 h-4" />
                Descargar PDF
              </a>
              <button
                type="button"
                onClick={openPrintable}
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition"
              >
                <Printer className="w-4 h-4" />
                Versión imprimible
              </button>
            </div>
          </div>

          <p className="mt-8 text-xs font-bold uppercase tracking-[0.28em] text-[#9ec5ff] print:text-[#1746a2]">
            Documento metodológico
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">
            Metodología de Diagnóstico Financiero
          </h1>
          <p className="mt-3 text-xl text-[#dbeafe] font-semibold print:text-[#123a72]">
            Dirección de Innovación Financiera
          </p>
          <p className="mt-5 text-[#dbeafe]/90 max-w-2xl leading-relaxed print:text-slate-600">
            Una metodología en dos fases para comprender el estado real de su empresa antes de
            proponer cualquier acompañamiento. Sin soluciones genéricas.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-12 space-y-10">
        {/* Intro */}
        <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-extrabold text-[#0b1f3f]">Introducción</h2>
          <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
            <p>Gracias por considerar nuestros servicios de Dirección de Innovación Financiera.</p>
            <p>
              Nuestro objetivo es convertirnos en un aliado estratégico para la toma de decisiones
              financieras de su empresa. Para ello, hemos diseñado una metodología en dos fases que
              nos permite comprender el estado actual de la organización antes de proponer cualquier
              servicio.
            </p>
            <p>
              Este enfoque garantiza que las recomendaciones respondan a las necesidades reales de
              la empresa y no a soluciones genéricas.
            </p>
          </div>
        </section>

        {/* FINANZAS 360 */}
        <section className="rounded-3xl border border-[#1746a2]/20 bg-[#e8f2ff] p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1746a2]">
            Identidad metodológica
          </p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0b1f3f]">
            Metodología F.I.N.A.N.Z.A.S. 360®
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            No vendemos un “diagnóstico gratuito” aislado. Aplicamos una metodología propia que
            estructura el entendimiento del negocio y la construcción de valor financiero.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {finanzas360.map((item, index) => (
              <div
                key={`${item.letter}-${index}`}
                className="flex items-start gap-3 rounded-2xl border border-white bg-white/80 px-4 py-3"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0b1f3f] text-sm font-bold text-white">
                  {item.letter}
                </span>
                <p className="text-sm font-semibold text-[#0b1f3f] pt-1.5">{item.title}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Confidencialidad + NDA */}
        <section className="rounded-3xl border border-amber-200/80 bg-amber-50/60 p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0b1f3f] text-white">
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-800">
                Confidencialidad y tratamiento de datos
              </p>
              <h2 className="mt-2 text-2xl font-extrabold text-[#0b1f3f]">
                Disclaimer de confidencialidad (NDA)
              </h2>
            </div>
          </div>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Toda la información compartida en el marco del Diagnóstico Financiero y de la
            Metodología F.I.N.A.N.Z.A.S. 360® será tratada bajo{" "}
            <strong>estricta confidencialidad</strong>. MOU SOLUTIONS S.A.S. (MEU Aliado) se
            compromete a no divulgar, comercializar ni utilizar los datos para fines distintos al
            diagnóstico y al acompañamiento acordado, conforme al Acuerdo de Confidencialidad (NDA)
            y a la Política de Tratamiento de Datos Personales.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            <strong>Importante:</strong> no envíe estados financieros ni documentación sensible hasta
            haber completado los pasos 1 y 2. Todo el proceso se gestiona a través de{" "}
            <a
              href={NDA_MAILTO}
              className="font-semibold text-[#1746a2] hover:underline"
            >
              colaboradores@meu-aliado.com
            </a>
            .
          </p>

          <h3 className="mt-6 text-lg font-extrabold text-[#0b1f3f]">Cómo iniciar el proceso</h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {confidentialitySteps.map((item) => (
              <article
                key={item.step}
                className="rounded-2xl border border-amber-200/70 bg-white p-5"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0b1f3f] text-sm font-bold text-white">
                  {item.step}
                </span>
                <h4 className="mt-3 font-bold text-[#123a72]">{item.title}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={NDA_PDF_PATH}
              download
              className="inline-flex items-center gap-2 rounded-full bg-[#0b1f3f] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#123a72] transition"
            >
              <Download className="w-4 h-4" />
              Descargar NDA (PDF)
            </a>
            <a
              href="/acuerdo-confidencialidad-nda.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-[#0b1f3f] hover:bg-slate-50 transition"
            >
              <FileText className="w-4 h-4" />
              Ver NDA
            </a>
            <a
              href={NDA_MAILTO}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#1746a2] bg-white px-5 py-2.5 text-sm font-bold text-[#1746a2] hover:bg-[#e8f2ff] transition"
            >
              <Mail className="w-4 h-4" />
              Escribir a colaboradores@
            </a>
          </div>
        </section>

        {/* FASE 1 */}
        <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Fase 1</p>
              <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-[#0b1f3f]">
                Diagnóstico Financiero Ejecutivo
              </h2>
            </div>
            <span className="rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-sm font-bold text-emerald-700">
              Sin costo
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
              <h3 className="font-extrabold text-[#0b1f3f]">Objetivo</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Realizar una evaluación inicial de la situación financiera de la empresa para
                identificar fortalezas, riesgos y oportunidades de mejora. Esta fase tiene un carácter
                exploratorio y permite determinar si existe una oportunidad de generar valor mediante
                un acompañamiento financiero especializado.
              </p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
              <h3 className="font-extrabold text-[#0b1f3f]">Duración</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Entre <strong>5 y 10 días hábiles</strong>.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#1746a2]">
                <ClipboardList className="w-4 h-4" />
                Entrada de bajo riesgo para la gerencia
              </div>
            </div>
          </div>

          <h3 className="mt-8 text-xl font-extrabold text-[#0b1f3f]">Información requerida</h3>
          <div className="mt-4 space-y-4">
            {phase1Sections.map((section) => (
              <article key={section.title} className="rounded-2xl border border-slate-200 p-5">
                <h4 className="font-bold text-[#123a72]">{section.title}</h4>
                <ul className="mt-2 space-y-1">
                  {section.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm text-slate-500">
                  <span className="font-semibold text-[#0b1f3f]">Finalidad: </span>
                  {section.purpose}
                </p>
              </article>
            ))}

            <article className="rounded-2xl border border-[#1746a2]/25 bg-[#f4f8fc] p-5">
              <h4 className="font-bold text-[#123a72]">9. Reunión con la gerencia</h4>
              <p className="mt-2 text-sm text-slate-600">
                Durante aproximadamente una hora profundizaremos en:
              </p>
              <ul className="mt-2 space-y-1">
                {[
                  "Los 5 problemas priorizados y su impacto en el negocio",
                  "Objetivos de crecimiento y decisiones pendientes",
                  "Procesos actuales y necesidades de la organización",
                  "Expectativas frente al acompañamiento financiero",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#1746a2] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <h3 className="mt-8 text-xl font-extrabold text-[#0b1f3f]">Entregables</h3>
          <p className="mt-2 text-sm text-slate-600">Al finalizar esta fase la empresa recibirá:</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {phase1Deliverables.map((item) => (
              <div key={item.title} className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                <p className="font-bold text-[#0b1f3f]">{item.title}</p>
                <p className="mt-1 text-sm text-slate-600">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FASE 2 */}
        <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1746a2]">Fase 2</p>
              <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-[#0b1f3f]">
                Diagnóstico Integral y Plan de Transformación Financiera
              </h2>
            </div>
            <span className="rounded-full bg-[#e8f2ff] border border-[#1746a2]/25 px-4 py-1.5 text-sm font-bold text-[#123a72]">
              Servicio profesional
            </span>
          </div>

          <p className="mt-5 text-slate-600 leading-relaxed">
            Una vez finalizada la fase inicial y si la empresa desea continuar, se desarrolla un
            diagnóstico integral con mayor profundidad para diseñar un plan de fortalecimiento
            financiero y acompañar su implementación.
          </p>

          <h3 className="mt-8 text-xl font-extrabold text-[#0b1f3f]">Alcance</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {phase2Scope.map((block) => (
              <article key={block.title} className="rounded-2xl border border-slate-200 p-4">
                <h4 className="font-bold text-[#123a72]">{block.title}</h4>
                <ul className="mt-2 space-y-1">
                  {block.items.map((item) => (
                    <li key={item} className="text-sm text-slate-600">
                      · {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <h3 className="mt-8 text-xl font-extrabold text-[#0b1f3f]">Información adicional requerida</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Dependiendo de las necesidades de la empresa, podrán solicitarse documentos como los
            siguientes. La solicitud se realizará de manera gradual y únicamente cuando sea necesaria
            para el desarrollo del diagnóstico.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {phase2ExtraDocs.map((doc) => (
              <span
                key={doc}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600"
              >
                {doc}
              </span>
            ))}
          </div>

          <h3 className="mt-8 text-xl font-extrabold text-[#0b1f3f]">Entregables</h3>
          <p className="mt-2 text-sm text-slate-600">
            La Fase 2 culmina con un <strong>Informe Integral de Diagnóstico Financiero</strong>, que
            incluye:
          </p>
          <ul className="mt-4 space-y-2">
            {phase2Deliverables.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                <FileText className="w-4 h-4 text-[#1746a2] shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Next step */}
        <section className="rounded-3xl bg-[#0b1f3f] text-white p-6 sm:p-8">
          <h2 className="text-2xl font-extrabold">Paso siguiente: Dirección de Innovación Financiera</h2>
          <p className="mt-4 text-[#dbeafe] leading-relaxed">
            Con base en el diagnóstico, se presenta una propuesta de acompañamiento continuo adaptada
            a las necesidades de la empresa. Este servicio puede incluir la participación de un
            Director Financiero Externo (Fractional CFO), apoyo en tesorería, planeación financiera,
            control de gestión, levantamiento de capital, relación con entidades financieras y
            fortalecimiento de la toma de decisiones estratégicas.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 print:hidden">
            <a
              href={cfo.whatsapp.diagnostico}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
            >
              Solicitar Diagnóstico Financiero Ejecutivo
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/empresas/direccion-de-innovacion-financiera/planes"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-6 py-3 text-sm font-bold text-white hover:bg-white/10 transition"
            >
              Ver planes
            </Link>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 print:hidden">
          <Link
            to="/empresas/direccion-de-innovacion-financiera"
            className="text-sm font-semibold text-[#1746a2] hover:underline"
          >
            ← Volver a Dirección de Innovación Financiera
          </Link>
          <div className="flex flex-wrap gap-3">
            <a
              href={PDF_PATH}
              download
              className="inline-flex items-center gap-2 rounded-full bg-[#0b1f3f] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#123a72] transition"
            >
              <Download className="w-4 h-4" />
              Descargar PDF
            </a>
            <button
              type="button"
              onClick={openPrintable}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-[#0b1f3f] hover:bg-slate-50 transition"
            >
              <Printer className="w-4 h-4" />
              Imprimir / Guardar PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiagnosticoFinanciero;
